import { NewsItem, Publication, Activity, Partner, HeroSlide, Video } from '../types';
import { LATEST_NEWS, PUBLICATIONS, PARTNERS, HERO_SLIDES, LATEST_VIDEOS, STAFF_MEMBERS, BRANCH_UPDATES } from '../constants';
import DOMPurify from 'dompurify';

const WP_API_URL = process.env.REACT_APP_WP_API_URL || 'https://www.lakpesdam.or.id/wp-json/wp/v2';
const WP_ROOT_URL = process.env.REACT_APP_WP_ROOT_URL || 'https://www.lakpesdam.or.id/wp-json';
const WP_GRAPHQL_URL = process.env.REACT_APP_WP_GRAPHQL_URL || 'https://www.lakpesdam.or.id/graphql';

const DATE_OPTIONS: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'long', year: 'numeric' };

export interface WPRestPost {
  id: number;
  slug: string;
  title: { rendered: string };
  excerpt: { rendered: string };
  content?: { rendered: string };
  date: string;
  type?: string;
  _embedded?: any;
  yoast_head_json?: any;
  jetpack_featured_media_url?: string;
  featured_image_src?: string;
  acf?: any;
}

/**
 * Helper kuat untuk mengekstrak URL dari field ACF File/Image.
 */
function resolveFileUrl(acfField: any): string {
  if (!acfField) return '';
  const data = Array.isArray(acfField) ? acfField[0] : acfField;
  if (!data) return '';
  if (typeof data === 'string') {
    if (data.startsWith('http')) return data;
    return '';
  }
  if (typeof data === 'object' && data !== null) {
    const url = data.url || data.source_url || data.link || data.guid || '';
    return typeof url === 'string' ? url : '';
  }
  return '';
}

/**
 * Helper untuk mengekstrak Featured Image secara robust dari REST API _embedded
 */
function extractFeaturedImage(wpPost: WPRestPost): string {
  if (!wpPost) return '';

  // 1. Coba dari _embedded (Standar REST API dengan parameter _embed)
  const embeddedImage = wpPost._embedded?.['wp:featuredmedia']?.[0];
  if (embeddedImage && !embeddedImage.code) {
    const url = embeddedImage.source_url ||
      embeddedImage.media_details?.sizes?.full?.source_url ||
      embeddedImage.media_details?.sizes?.large?.source_url;

    if (url) return url;
  }

  // 2. Fallback dari Yoast SEO jika akses REST ke media dilarang (rest_forbidden)
  if (wpPost.yoast_head_json?.og_image?.[0]?.url) return wpPost.yoast_head_json.og_image[0].url;

  // 3. Coba dari jetpack_featured_media_url (Jika ada plugin Jetpack)
  if (wpPost.jetpack_featured_media_url) return wpPost.jetpack_featured_media_url;

  // 4. Coba dari field featured_image_src (Sering ditambahkan oleh beberapa tema/plugin)
  if (wpPost.featured_image_src) return wpPost.featured_image_src;

  // 5. Fallback ke gambar dari ACF jika kebetulan ada field gambar manual
  if (wpPost.acf?.thumbnail || wpPost.acf?.image || wpPost.acf?.foto) {
    return resolveFileUrl(wpPost.acf.thumbnail || wpPost.acf.image || wpPost.acf.foto);
  }

  return '';
}

/**
 * Keamanan: Sanitasi tinggi berbasis DOMPurify untuk HTML dari WordPress.
 * Konfigurasi ini mengizinkan tag dasar format teks dan iframe (untuk video YouTube dsb), 
 * tapi secara absolut memblokir eksekusi script berbahaya (XSS).
 */
export function sanitizeHTML(html: string): string {
  if (!html) return '';
  const sanitized = DOMPurify.sanitize(html, {
    ADD_TAGS: ['iframe', 'blockquote'],
    ADD_ATTR: ['allow', 'allowfullscreen', 'frameborder', 'scrolling', 'target']
  });

  // Decode common WordPress typographical HTML entities (wptexturize)
  return sanitized
    .replace(/&#8216;/g, "'")
    .replace(/&#8217;/g, "'")
    .replace(/&#8220;/g, '"')
    .replace(/&#8221;/g, '"')
    .replace(/&#8211;/g, '-')
    .replace(/&#8212;/g, '--')
    .replace(/&#038;/g, '&')
    .replace(/&#38;/g, '&')
    .replace(/&amp;/g, '&')
    .replace(/&quot;/g, '"');
}

// Simple in-memory cache with max size limit
const cache = new Map<string, { data: any; expiry: number }>();
const CACHE_DURATION = 300000; // 5 minutes
const MAX_CACHE_SIZE = 100;

async function fetchAPI(endpoint: string, options: RequestInit = {}, token: string | null = null, retries = 2): Promise<any> {
  const cleanEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  let url = `${WP_API_URL}${cleanEndpoint}`;

  // Check cache for GET requests without token
  const isCacheable = (options.method === 'GET' || !options.method) && !token;
  if (isCacheable) {
    const cached = cache.get(url);
    if (cached && cached.expiry > Date.now()) return cached.data;
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 25000);

  try {
    const headers: any = {
      'Content-Type': 'application/json',
      ...options.headers
    };

    if (token) headers['Authorization'] = token;

    let res = await fetch(url, {
      signal: controller.signal,
      headers: headers,
      method: options.method || 'GET',
      body: options.body
    });

    clearTimeout(timeoutId);

    if (!res.ok) {
      if (res.status === 401) return null;
      return null;
    }

    const data = await res.json();

    if (isCacheable && data) {
      if (cache.size >= MAX_CACHE_SIZE) {
        const oldestKey = cache.keys().next().value;
        if (oldestKey) cache.delete(oldestKey);
      }
      cache.set(url, { data, expiry: Date.now() + CACHE_DURATION });
    }

    return data;
  } catch (error: any) {
    clearTimeout(timeoutId);
    if (retries > 0 && error.name !== 'AbortError') {
      await new Promise(res => setTimeout(res, 1000 * (3 - retries)));
      return fetchAPI(endpoint, options, token, retries - 1);
    }
    return null;
  }
}

async function fetchGraphQL(query: string, variables = {}, retries = 2): Promise<any> {
  const cacheKey = `${WP_GRAPHQL_URL}_${query}_${JSON.stringify(variables)}`;
  const cached = cache.get(cacheKey);
  if (cached && cached.expiry > Date.now()) return cached.data;

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 25000);

  try {
    const res = await fetch(WP_GRAPHQL_URL, {
      method: 'POST',
      signal: controller.signal,
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ query, variables })
    });
    clearTimeout(timeoutId);
    const json = await res.json();
    if (json.data) {
      if (cache.size >= MAX_CACHE_SIZE) {
        const oldestKey = cache.keys().next().value;
        if (oldestKey) cache.delete(oldestKey);
      }
      cache.set(cacheKey, { data: json.data, expiry: Date.now() + CACHE_DURATION });
    }
    return json.data;
  } catch (e) {
    clearTimeout(timeoutId);
    if (retries > 0 && (e as any).name !== 'AbortError') {
      await new Promise(res => setTimeout(res, 1000 * (3 - retries)));
      return fetchGraphQL(query, variables, retries - 1);
    }
    return null;
  }
}

export async function loginUser(username: string, appPassword: string) {
  const cleanPassword = appPassword.replace(/\s/g, '');
  const basicToken = 'Basic ' + btoa(`${username}:${cleanPassword}`);
  try {
    const res = await fetch(`${WP_API_URL}/users/me`, { headers: { 'Authorization': basicToken } });
    if (res.ok) {
      const data = await res.json();
      if (data.id) return basicToken;
    }
  } catch (e) {
    console.error('[wordpress] Basic auth login failed:', e);
  }
  try {
    const res = await fetch(`${WP_ROOT_URL}/jwt-auth/v1/token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password: appPassword })
    });
    const data = await res.json();
    if (res.ok && data.token) return `Bearer ${data.token}`;
  } catch (e) {
    console.error('[wordpress] JWT auth login failed:', e);
  }
  return null;
}

export async function getCurrentUser(token: string) {
  if (token.startsWith('DEMO_')) return { name: 'Demo Admin', roles: ['administrator'], avatar_urls: { '96': 'https://ui-avatars.com/api/?name=Admin' } };
  return await fetchAPI('/users/me?context=edit', {}, token);
}

export async function uploadMedia(file: File, token: string) {
  const formData = new FormData();
  formData.append('file', file);
  const headers: any = {};
  if (token) headers['Authorization'] = token;
  const res = await fetch(`${WP_API_URL}/media`, { method: 'POST', headers, body: formData });
  const data = await res.json();
  if (!res.ok) {
    // Lempar error dengan pesan dari WordPress agar bisa ditampilkan ke user
    const msg = data?.message || data?.code || `Upload gagal (HTTP ${res.status})`;
    throw new Error(msg);
  }
  return data;
}

export async function savePost(data: any, token: string) {
  const isUpdate = !!data.id;
  const baseEndpoint = data.isCPT ? `/${data.categorySlug}` : '/posts';
  const endpoint = isUpdate ? `${baseEndpoint}/${data.id}` : baseEndpoint;

  // Pisahkan field internal dari body — WordPress REST API menolak field yang tidak dikenal (400)
  const { id: _id, categorySlug: _cs, isCPT: _isCPT, ...wpPayload } = data;

  return await fetchAPI(endpoint, { method: 'POST', body: JSON.stringify(wpPayload) }, token);
}

export async function deletePost(id: string, token: string) {
  return await fetchAPI(`/posts/${id}`, { method: 'DELETE' }, token);
}

export async function getHeroSlides(): Promise<HeroSlide[]> {
  // Try GraphQL first — tambahkan slug
  const query = `query GetHeroSlides { posts(first: 5, where: {tag: "hero"}) { nodes { databaseId slug title excerpt categories { nodes { name } } featuredImage { node { sourceUrl } } } } }`;
  const data = await fetchGraphQL(query);
  if (data && data.posts && data.posts.nodes && data.posts.nodes.length > 0) {
    return data.posts.nodes.map((post: any) => ({
      id: post.databaseId.toString(),
      slug: post.slug || post.databaseId.toString(),
      title: sanitizeHTML(post.title),
      subtitle: post.excerpt ? sanitizeHTML(post.excerpt).replace(/<[^>]+>/g, '') : '',
      category: post.categories?.nodes[0]?.name || 'Unggulan',
      imageUrl: post.featuredImage?.node?.sourceUrl || '',
    }));
  }

  // Fallback: Try REST API with tag "hero"
  try {
    const tags = await fetchAPI('/tags?slug=hero');
    if (Array.isArray(tags) && tags.length > 0) {
      const posts = await fetchAPI(`/posts?tags=${tags[0].id}&per_page=5&_embed`);
      if (Array.isArray(posts) && posts.length > 0) {
        return posts.map((post: any) => ({
          id: post.id.toString(),
          slug: post.slug || post.id.toString(),
          title: sanitizeHTML(post.title.rendered),
          subtitle: post.excerpt?.rendered ? sanitizeHTML(post.excerpt.rendered).replace(/<[^>]+>/g, '').slice(0, 200) : '',
          category: post._embedded?.['wp:term']?.[0]?.[0]?.name || 'Unggulan',
          imageUrl: extractFeaturedImage(post),
        }));
      }
    }
  } catch (e) {
    console.error('[wordpress] REST API fallback for hero slides failed:', e);
  }

  // Final fallback: static data
  return HERO_SLIDES.map(s => ({ ...s, isFallback: true }));
}

export async function getPartners(): Promise<Partner[]> {
  // Try GraphQL first
  const query = `query GetPartners { posts(where: {categoryName: "mitra"}, first: 20) { nodes { databaseId title dataMitra { websiteUrl } featuredImage { node { sourceUrl } } } } }`;
  const data = await fetchGraphQL(query);
  if (data && data.posts && data.posts.nodes && data.posts.nodes.length > 0) {
    return data.posts.nodes.map((item: any) => ({
      id: item.databaseId.toString(),
      name: item.title,
      logoUrl: item.featuredImage?.node?.sourceUrl || '',
      website: item.dataMitra?.websiteUrl || item.dataMitra?.url || '#'
    }));
  }

  // Fallback: Try REST API with category "mitra"
  try {
    const cats = await fetchAPI('/categories?slug=mitra');
    if (Array.isArray(cats) && cats.length > 0) {
      const posts = await fetchAPI(`/posts?categories=${cats[0].id}&per_page=20&_embed`);
      if (Array.isArray(posts) && posts.length > 0) {
        return posts.map((item: any) => ({
          id: item.id.toString(),
          name: item.title.rendered,
          logoUrl: extractFeaturedImage(item),
          website: item.acf?.website_url || item.acf?.websiteUrl || item.acf?.url || '#'
        }));
      }
    }
  } catch (e) {
    console.error('[wordpress] REST API fallback for partners failed:', e);
  }

  // Final fallback: static data
  return PARTNERS.map(p => ({ ...p, isFallback: true }));
}

export async function getPostsByCategory(categorySlug: string, limit: number = 6): Promise<NewsItem[]> {
  // Try GraphQL first — tambahkan slug
  const query = `query GetByCat($slug: String, $limit: Int) { posts(where: {categoryName: $slug}, first: $limit) { nodes { databaseId slug title date excerpt categories { nodes { name } } featuredImage { node { sourceUrl } } author { node { name } } } } }`;
  const data = await fetchGraphQL(query, { slug: categorySlug, limit });
  if (data && data.posts && data.posts.nodes && data.posts.nodes.length > 0) {
    return data.posts.nodes.map((p: any) => ({
      id: p.databaseId.toString(),
      slug: p.slug || p.databaseId.toString(),
      title: sanitizeHTML(p.title),
      date: new Date(p.date).toLocaleDateString('id-ID', DATE_OPTIONS),
      category: p.categories?.nodes[0]?.name || 'Umum',
      imageUrl: p.featuredImage?.node?.sourceUrl || '',
      excerpt: p.excerpt ? sanitizeHTML(p.excerpt).replace(/<[^>]+>/g, '') : '',
      author_name: p.author?.node?.name || 'Admin'
    }));
  }

  // Fallback: REST API — find category by slug, then fetch posts
  try {
    const cats = await fetchAPI(`/categories?slug=${categorySlug}`);
    if (Array.isArray(cats) && cats.length > 0) {
      const posts = await fetchAPI(`/posts?categories=${cats[0].id}&per_page=${limit}&_embed`);
      if (Array.isArray(posts) && posts.length > 0) {
        return posts.map((post: any) => ({
          id: post.id.toString(),
          slug: post.slug || post.id.toString(),
          title: sanitizeHTML(post.title.rendered),
          date: new Date(post.date).toLocaleDateString('id-ID', DATE_OPTIONS),
          category: post._embedded?.['wp:term']?.[0]?.[0]?.name || 'Umum',
          imageUrl: extractFeaturedImage(post),
          excerpt: post.excerpt?.rendered ? sanitizeHTML(post.excerpt.rendered).replace(/<[^>]+>/g, '') : '',
          author_name: post._embedded?.author?.[0]?.name || 'Admin'
        }));
      }
    }
  } catch (e) {
    console.error(`[wordpress] REST API fallback for category "${categorySlug}" failed:`, e);
  }

  return [];
}

export async function getLatestNews(page: number = 1, perPage: number = 3): Promise<NewsItem[]> {
  const cats = await fetchAPI('/categories?slug=berita');
  if (!Array.isArray(cats) || cats.length === 0) return page === 1 ? LATEST_NEWS.map(n => ({ ...n, isFallback: true })) : [];
  const categoryId = cats[0].id;
  const posts = await fetchAPI(`/posts?categories=${categoryId}&per_page=${perPage}&page=${page}&_embed`);
  if (!Array.isArray(posts) || posts.length === 0) return page === 1 ? LATEST_NEWS.map(n => ({ ...n, isFallback: true })) : [];
  return posts.map((post: any) => ({
    id: post.id.toString(),
    slug: post.slug || post.id.toString(),
    title: sanitizeHTML(post.title.rendered),
    date: new Date(post.date).toLocaleDateString('id-ID', DATE_OPTIONS),
    category: post._embedded?.['wp:term']?.[0]?.[0]?.name || 'Berita',
    imageUrl: extractFeaturedImage(post),
    excerpt: sanitizeHTML(post.excerpt?.rendered || '').replace(/<[^>]+>/g, '').slice(0, 25) + '...'
  }));
}

export async function getPostById(id: string): Promise<NewsItem | null> {
  // Parallel fetch for different post types
  const requests = [
    fetchAPI(`/posts/${id}?_embed`),
    fetchAPI(`/publications/${id}?_embed`),
    fetchAPI(`/pages/${id}?_embed`)
  ];

  const results = await Promise.all(requests);
  const post = results.find(result => result !== null && result.id);

  if (!post) return null;

  // Coba resolve PDF URL dari semua kemungkinan field ACF
  let pdfField = post.acf?.pdf_url || post.acf?.download_url || post.acf?.file_url;
  let pdfLink = resolveFileUrl(pdfField);

  // Tangani kasus ACF mengembalikan File ID (integer) alih-alih URL
  if (!pdfLink && typeof pdfField === 'number') {
    try {
      const media = await fetchAPI(`/media/${pdfField}`);
      if (media?.source_url) pdfLink = media.source_url;
    } catch (e) {
      console.error('[wordpress] getPostById: Gagal resolve media ID dari ACF:', pdfField, e);
    }
  }

  // Kategori: untuk CPT publications, coba dari ACF dulu, lalu dari _embedded terms
  const category =
    post._embedded?.['wp:term']?.[0]?.[0]?.name ||
    post.acf?.category ||
    (post.type === 'publications' ? 'Buku & Kajian' : null) ||
    (post.type === 'page' ? 'Profil' : 'Umum');

  return {
    id: post.id.toString(),
    slug: post.slug || post.id.toString(),
    title: sanitizeHTML(post.title.rendered),
    date: new Date(post.date).toLocaleDateString('id-ID', DATE_OPTIONS),
    category,
    imageUrl: extractFeaturedImage(post),
    excerpt: sanitizeHTML(post.excerpt?.rendered || post.content?.rendered || '').slice(0, 200),
    content: sanitizeHTML(post.content?.rendered || post.excerpt?.rendered || ''),
    author_name: post._embedded?.author?.[0]?.name || post.acf?.author_name || 'Tim Lakpesdam',
    pdfUrl: pdfLink || '',
    downloadUrl: pdfLink || '',
    is_downloadable: post.acf?.is_downloadable !== false,
    type: post.acf?.type || (pdfLink ? 'Dokumen' : '')
  };
}

/**
 * Mengambil post berdasarkan slug URL.
 * Mencoba posts, publications, dan pages secara paralel.
 * Ini adalah fungsi utama untuk halaman detail post dengan URL berbasis judul.
 */
export async function getPostBySlug(slug: string): Promise<any | null> {
  // Coba semua endpoint secara paralel
  const requests = [
    fetchAPI(`/posts?slug=${encodeURIComponent(slug)}&_embed`),
    fetchAPI(`/publications?slug=${encodeURIComponent(slug)}&_embed`),
    fetchAPI(`/pages?slug=${encodeURIComponent(slug)}&_embed`),
  ];

  const results = await Promise.all(requests);

  // WordPress mengembalikan array saat query by slug
  let post: any = null;
  for (const result of results) {
    if (Array.isArray(result) && result.length > 0) {
      post = result[0];
      break;
    }
  }

  // Jika tidak ditemukan via query slug dan slug berupa angka (ID)
  if (!post && /^\d+$/.test(slug)) {
    try {
      const byId = await getPostById(slug);
      if (byId) return byId;
    } catch (e) {
      console.warn('[wordpress] getPostBySlug fallback by ID failed:', e);
    }
  }

  // Jika masih belum ditemukan (misal data statis offline / fallback)
  if (!post) {
    const cleanSlug = slug.toLowerCase();
    const fallbackPub = PUBLICATIONS.find(p =>
      p.id.toLowerCase() === cleanSlug ||
      (p.slug && p.slug.toLowerCase() === cleanSlug) ||
      p.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') === cleanSlug
    );
    if (fallbackPub) {
      return {
        id: fallbackPub.id,
        slug: fallbackPub.slug || fallbackPub.id,
        title: fallbackPub.title,
        date: fallbackPub.date,
        category: fallbackPub.category || 'Riset & Publikasi',
        imageUrl: fallbackPub.imageUrl || '',
        excerpt: fallbackPub.excerpt || '',
        content: fallbackPub.content || `<p>${fallbackPub.excerpt}</p><p>Kajian dan dokumen publikasi ini diterbitkan oleh Lakpesdam PBNU dalam rangka penguatan literasi dan advokasi kebijakan berbasis bukti.</p>`,
        author_name: fallbackPub.author?.name || 'Tim Riset Lakpesdam',
        pdfUrl: fallbackPub.pdfUrl || '',
        downloadUrl: fallbackPub.downloadUrl || fallbackPub.pdfUrl || '',
        is_downloadable: fallbackPub.is_downloadable !== false,
        type: fallbackPub.type || 'Buku',
        isFallback: true
      };
    }

    const fallbackNews = [...LATEST_NEWS, ...BRANCH_UPDATES, ...HERO_SLIDES].find((n: any) =>
      n.id.toLowerCase() === cleanSlug ||
      (n.slug && n.slug.toLowerCase() === cleanSlug) ||
      n.title?.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') === cleanSlug
    );
    if (fallbackNews) {
      return {
        id: fallbackNews.id,
        slug: (fallbackNews as any).slug || fallbackNews.id,
        title: fallbackNews.title,
        date: (fallbackNews as any).date || '1 Januari 2024',
        category: fallbackNews.category || 'Berita',
        imageUrl: fallbackNews.imageUrl || '',
        excerpt: (fallbackNews as any).excerpt || (fallbackNews as any).subtitle || '',
        content: (fallbackNews as any).content || `<p>${(fallbackNews as any).excerpt || (fallbackNews as any).subtitle || ''}</p>`,
        author_name: (fallbackNews as any).author_name || 'Tim Lakpesdam',
        pdfUrl: '',
        downloadUrl: '',
        is_downloadable: false,
        type: 'Berita',
        isFallback: true
      };
    }

    return null;
  }

  // Resolve PDF dari ACF
  let pdfField = post.acf?.pdf_url || post.acf?.download_url || post.acf?.file_url;
  let pdfLink = resolveFileUrl(pdfField);

  if (!pdfLink && typeof pdfField === 'number') {
    try {
      const media = await fetchAPI(`/media/${pdfField}`);
      if (media?.source_url) pdfLink = media.source_url;
    } catch (e) {
      console.error('[wordpress] getPostBySlug: Gagal resolve media ID dari ACF:', pdfField, e);
    }
  }

  const category =
    post._embedded?.['wp:term']?.[0]?.[0]?.name ||
    post.acf?.category ||
    (post.type === 'publications' ? 'Buku & Kajian' : null) ||
    (post.type === 'page' ? 'Profil' : 'Umum');

  const tags = post._embedded?.['wp:term']?.[1]?.map((t: any) => t.slug) || [];

  return {
    id: post.id.toString(),
    slug: post.slug || post.id.toString(),
    title: sanitizeHTML(post.title.rendered),
    date: new Date(post.date).toLocaleDateString('id-ID', DATE_OPTIONS),
    category,
    tags,
    imageUrl: extractFeaturedImage(post),
    excerpt: sanitizeHTML(post.excerpt?.rendered || post.content?.rendered || '').slice(0, 200),
    content: sanitizeHTML(post.content?.rendered || post.excerpt?.rendered || ''),
    author_name: post._embedded?.author?.[0]?.name || post.acf?.author_name || 'Tim Lakpesdam',
    pdfUrl: pdfLink || '',
    downloadUrl: pdfLink || '',
    is_downloadable: post.acf?.is_downloadable !== false,
    type: post.acf?.type || (pdfLink ? 'Dokumen' : '')
  };
}

export async function getPublications(): Promise<Publication[]> {
  try {
    const data = await fetchAPI('/publications?per_page=50&_embed');
    if (!Array.isArray(data) || data.length === 0) return PUBLICATIONS.map(p => ({ ...p, isFallback: true }));

    // Gunakan Promise.all untuk mengambil data media secara asinkron
    // dalam kasus User mengatur kembalian ACF sebagai File ID (Integer) 
    const publicationsList = await Promise.all(data.map(async (item: any) => {
      let pdfField = item.acf?.pdf_url || item.acf?.download_url || item.acf?.file_url;
      let pdfLink = resolveFileUrl(pdfField);

      // Mengatasi Return Value berupa 'File ID' (Integer) dari ACF 'Detail Publikasi'
      if (!pdfLink && typeof pdfField === 'number') {
        try {
          const media = await fetchAPI(`/media/${pdfField}`);
          if (media && media.source_url) {
            pdfLink = media.source_url;
          }
        } catch (e) {
          console.error("Gagal menarik data media dari ID ACF: " + pdfField, e);
        }
      }

      return {
        id: item.id.toString(),
        slug: item.slug || item.id.toString(),
        title: sanitizeHTML(item.title.rendered),
        excerpt: sanitizeHTML(item.excerpt?.rendered || '').replace(/<[^>]+>/g, ''),
        content: sanitizeHTML(item.content?.rendered || ''),
        author: { id: item.author.toString(), name: item.acf?.author_name || 'Tim Riset Lakpesdam', role: 'Peneliti', imageUrl: '', profileLink: '#' },
        date: new Date(item.date).toLocaleDateString('id-ID', DATE_OPTIONS),
        year: parseInt(item.acf?.year) || new Date(item.date).getFullYear(),
        category: item.acf?.category || 'Umum',
        type: (item.acf?.type as any) || 'Buku',
        imageUrl: extractFeaturedImage(item),
        pdfUrl: pdfLink || '#',
        is_downloadable: item.acf?.is_downloadable !== false,
        downloadUrl: pdfLink || '#'
      };
    }));
    return publicationsList;
  } catch (e) { return PUBLICATIONS.map(p => ({ ...p, isFallback: true })); }
}

export async function getYoutubeVideos(): Promise<Video[]> {
  const channelId = 'UCAXV9NWByHzVO4e6g5fUcyQ';

  // Hindari memanggil proxy eksternal saat proses build statis (react-snap)
  if (typeof navigator !== 'undefined' && navigator.userAgent === 'ReactSnap') {
    return LATEST_VIDEOS.map(v => ({ ...v, isFallback: true }));
  }

  // Cache di sessionStorage selama 30 menit agar tidak memanggil proxy berulang
  const SESSION_KEY = 'yt_videos_cache';
  const SESSION_TTL = 30 * 60 * 1000; // 30 menit
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (raw) {
      const { data, expiry } = JSON.parse(raw);
      if (expiry > Date.now() && Array.isArray(data) && data.length > 0) {
        return data;
      }
    }
  } catch (_) { }

  // Strategi 1: WordPress server-side proxy (NO CORS issue — recommended for production)
  try {
    const wpProxyUrl = `${WP_ROOT_URL}/lakpesdam/v1/youtube-videos`;
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000);
    const res = await fetch(wpProxyUrl, { signal: controller.signal });
    clearTimeout(timeoutId);

    if (res.ok) {
      const videos: Video[] = await res.json();
      if (Array.isArray(videos) && videos.length > 0 && videos[0].id) {
        try {
          sessionStorage.setItem(SESSION_KEY, JSON.stringify({ data: videos, expiry: Date.now() + SESSION_TTL }));
        } catch (_) { }
        return videos;
      }
    }
  } catch (_) {
    // WP proxy tidak tersedia — lanjut ke CORS proxy fallback
  }

  // Strategi 2: CORS proxy pihak ketiga (fallback — terutama untuk local development)
  const rssUrl = `https://www.youtube.com/feeds/videos.xml?channel_id=${channelId}`;
  const proxies = [
    { url: `https://api.allorigins.win/get?url=${encodeURIComponent(rssUrl)}`, type: 'json' as const },
    { url: `https://corsproxy.io/?${encodeURIComponent(rssUrl)}`, type: 'text' as const },
  ];

  function parseRssEntries(textData: string): Video[] | null {
    if (!textData ||
      textData.trim().toLowerCase().startsWith('<!doctype') ||
      textData.trim().toLowerCase().startsWith('<html')) return null;

    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(textData, 'text/xml');
    if (xmlDoc.getElementsByTagName('parseerror').length > 0 ||
      xmlDoc.getElementsByTagName('parsererror').length > 0) return null;

    const entries = Array.from(xmlDoc.querySelectorAll('entry')).slice(0, 3);
    if (entries.length === 0) return null;

    return entries.map(entry => {
      const videoId = entry.querySelector('videoId')?.textContent || '';
      const title = entry.querySelector('title')?.textContent || '';
      const link = entry.querySelector('link')?.getAttribute('href') || `https://www.youtube.com/watch?v=${videoId}`;
      const pubDate = entry.querySelector('published')?.textContent || '';
      let thumbnailUrl = '';
      const mediaGroup = entry.getElementsByTagNameNS('*', 'group')[0];
      if (mediaGroup) {
        const thumbnail = mediaGroup.getElementsByTagNameNS('*', 'thumbnail')[0];
        if (thumbnail) thumbnailUrl = thumbnail.getAttribute('url') || '';
      }
      if (!thumbnailUrl && videoId) thumbnailUrl = `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`;
      return { id: videoId, title, url: link, thumbnailUrl, date: new Date(pubDate).toLocaleDateString('id-ID') };
    });
  }

  for (const proxy of proxies) {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 8000);
      const res = await fetch(proxy.url, { signal: controller.signal });
      clearTimeout(timeoutId);
      if (!res.ok) continue;

      let textData = '';
      if (proxy.type === 'json') {
        const data = await res.json();
        textData = data.contents || '';
      } else {
        textData = await res.text();
      }

      const videos = parseRssEntries(textData);
      if (videos && videos.length > 0) {
        try {
          sessionStorage.setItem(SESSION_KEY, JSON.stringify({ data: videos, expiry: Date.now() + SESSION_TTL }));
        } catch (_) { }
        return videos;
      }
    } catch (_) {
      // Lanjut ke proxy berikutnya tanpa log error berlebihan
    }
  }

  // Strategi 3: Fallback terakhir — gunakan data statis
  return LATEST_VIDEOS.map(v => ({ ...v, isFallback: true }));
}

export async function getHitStats() {
  try {
    if (typeof navigator !== 'undefined' && navigator.userAgent === 'ReactSnap') {
      return { daily: {}, monthly: {}, yearly: {} };
    }
    const API_URL = process.env.REACT_APP_WP_API_URL || 'https://www.lakpesdam.or.id/wp-json/wp/v2';
    const baseDomain = API_URL.replace('/wp/v2', '');
    const res = await fetch(`${baseDomain}/lakpesdam/v1/stats`);
    if (!res.ok) return { daily: {}, monthly: {}, yearly: {} };
    return await res.json();
  } catch (e) {
    return { daily: {}, monthly: {}, yearly: {} };
  }
}

export async function trackHit() {
  try {
    if (typeof navigator !== 'undefined' && navigator.userAgent === 'ReactSnap') return;
    const API_URL = process.env.REACT_APP_WP_API_URL || 'https://www.lakpesdam.or.id/wp-json/wp/v2';
    const baseDomain = API_URL.replace('/wp/v2', '');

    // Tarik geolokasi sederhana secara asinkron dari browser
    let city = 'Unknown', country = 'Unknown';
    try {
      const geoRes = await fetch('https://ipapi.co/json/');
      const geoData = await geoRes.json();
      city = geoData.city || 'Unknown';
      country = geoData.country_name || 'Unknown';
    } catch (err) {
      console.warn('[wordpress] Geo lookup failed:', err);
    }

    await fetch(`${baseDomain}/lakpesdam/v1/track`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ city, country })
    });
  } catch (e) { }
}

export async function getHistoryPost(): Promise<NewsItem | null> {
  const pages = await fetchAPI('/pages?slug=sejarah&_embed');
  if (!Array.isArray(pages) || pages.length === 0) return null;
  const p = pages[0];
  return {
    id: p.id.toString(),
    title: sanitizeHTML(p.title?.rendered || ''),
    date: new Date(p.date).toLocaleDateString('id-ID', DATE_OPTIONS),
    category: 'Sejarah',
    imageUrl: extractFeaturedImage(p),
    excerpt: sanitizeHTML(p.excerpt?.rendered || ''),
    content: sanitizeHTML(p.content?.rendered || '')
  };
}

export async function getStaffMembers(): Promise<NewsItem[]> {
  // Hindari timeout saat react-snap berjalan
  if (typeof navigator !== 'undefined' && navigator.userAgent === 'ReactSnap') {
    return [];
  }

  const sortStaff = (a: NewsItem, b: NewsItem) => {
    // Preamble: kelompokkan yang memiliki gambar/excerpt di atas
    const aHasImg = a.imageUrl ? 1 : 0;
    const aHasQuote = (a.quote || a.excerpt) ? 1 : 0;
    const bHasImg = b.imageUrl ? 1 : 0;
    const bHasQuote = (b.quote || b.excerpt) ? 1 : 0;

    const aTopTier = (aHasImg || aHasQuote) ? 1 : 0;
    const bTopTier = (bHasImg || bHasQuote) ? 1 : 0;

    if (aTopTier !== bTopTier) {
      return bTopTier - aTopTier;
    }

    // 1. Urutan staf dari yang terkecil di atas
    const orderA = a.order || 999;
    const orderB = b.order || 999;
    if (orderA !== orderB) {
      return orderA - orderB;
    }

    // 2. Sort dari Alphabetic nya
    const titleA = (a.title || '').toLowerCase();
    const titleB = (b.title || '').toLowerCase();
    if (titleA !== titleB) {
      return titleA < titleB ? -1 : 1;
    }

    // 3. Prioritaskan yang ada foto jika masih seri
    if (aHasImg !== bHasImg) {
      return bHasImg - aHasImg;
    }

    return 0;
  };

  // Primary: GraphQL query for StaffMembers CPT with InformasiStaff ACF fields
  const query = `query GetStaffMembers {
    allStaffMembers(first: 50) {
      nodes {
        databaseId
        title
        date
        featuredImage { node { sourceUrl } }
        ... on StaffMembers {
          informasiStaff {
            jabatan
            nama
            qu
            newField
          }
        }
      }
    }
  }`;

  try {
    const data = await fetchGraphQL(query);
    if (data && data.allStaffMembers && data.allStaffMembers.nodes && data.allStaffMembers.nodes.length > 0) {
      const mappedPosts: NewsItem[] = data.allStaffMembers.nodes.map((post: any) => ({
        id: post.databaseId.toString(),
        title: sanitizeHTML(post.title || ''),
        date: new Date(post.date).toLocaleDateString('id-ID', DATE_OPTIONS),
        category: 'Staff',
        imageUrl: post.featuredImage?.node?.sourceUrl || '',
        excerpt: '',
        role: post.informasiStaff?.jabatan || 'Staf Eksekutif',
        responsibility: post.informasiStaff?.nama || '',
        quote: post.informasiStaff?.qu || '',
        order: parseInt(post.informasiStaff?.newField) || 999
      }));
      return mappedPosts.sort(sortStaff);
    }
  } catch (e) {
    console.error('[wordpress] GraphQL allStaffMembers query failed:', e);
  }

  // Fallback 1: REST API CPT endpoint
  const posts = await fetchAPI(`/staff_member?per_page=50&_embed`);

  if (Array.isArray(posts) && posts.length > 0) {
    const mappedPosts: NewsItem[] = posts.map((post: any) => ({
      id: post.id.toString(),
      title: sanitizeHTML(post.title.rendered),
      date: new Date(post.date).toLocaleDateString('id-ID', DATE_OPTIONS),
      category: 'Staff',
      imageUrl: extractFeaturedImage(post),
      excerpt: sanitizeHTML(post.excerpt?.rendered || '').replace(/<[^>]+>/g, ''),
      role: post.acf?.jabatan || 'Staf Eksekutif',
      responsibility: post.acf?.tanggung_jawab || post.acf?.nama || '',
      quote: post.acf?.quote || post.acf?.qu || '',
      order: parseInt(post.acf?.urutan) || parseInt(post.acf?.newField) || 999
    }));
    return mappedPosts.sort(sortStaff);
  }

  // Fallback 2: Category "staff" or "staf" from regular posts
  try {
    const staffCat = await fetchAPI('/categories?slug=staff');
    const stafCat = await fetchAPI('/categories?slug=staf');
    const cat = (Array.isArray(staffCat) && staffCat.length > 0) ? staffCat[0]
      : (Array.isArray(stafCat) && stafCat.length > 0) ? stafCat[0]
        : null;

    if (cat) {
      const catPosts = await fetchAPI(`/posts?categories=${cat.id}&per_page=50&_embed`);
      if (Array.isArray(catPosts) && catPosts.length > 0) {
        const mappedPosts: NewsItem[] = catPosts.map((post: any) => ({
          id: post.id.toString(),
          title: sanitizeHTML(post.title.rendered),
          date: new Date(post.date).toLocaleDateString('id-ID', DATE_OPTIONS),
          category: 'Staff',
          imageUrl: extractFeaturedImage(post),
          excerpt: sanitizeHTML(post.excerpt?.rendered || '').replace(/<[^>]+>/g, ''),
          role: post._embedded?.['wp:term']?.[0]?.[0]?.name || 'Staf Operasional',
          responsibility: post.acf?.tanggung_jawab || post.acf?.nama || '',
          quote: post.acf?.quote || post.acf?.qu || '',
          order: parseInt(post.acf?.urutan) || parseInt(post.acf?.newField) || 999
        }));
        return mappedPosts.sort(sortStaff);
      }
    }
  } catch (e) {
    console.error('[wordpress] REST API fallback for staff members failed:', e);
  }

  return [];
}
// =========================================================================
// PROGRAM INKLUSI SERVICES
// =========================================================================

export function slugify(text: string): string {
  if (!text) return '';
  return text
    .toString()
    .toLowerCase()
    .replace(/<[^>]+>/g, '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');
}

/**
 * Shared formatter
 */
function formatNewsItem(
  post: any,
  categoryLabel: string,
  excerptLength: number
): NewsItem {
  const rawTitle = post.title?.rendered ?? post.title ?? '';
  let cleanSlug = post.slug;
  if (!cleanSlug || /^\d+$/.test(cleanSlug)) {
    const fromTitle = slugify(rawTitle);
    cleanSlug = fromTitle || (post.databaseId ? post.databaseId.toString() : post.id?.toString() || '');
  }

  return {
    id: post.databaseId
      ? post.databaseId.toString()
      : post.id.toString(),
    slug: cleanSlug,
    title: rawTitle,
    date: new Date(post.date).toLocaleDateString('id-ID', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }),
    category: categoryLabel,
    imageUrl:
      post.featuredImage?.node?.sourceUrl ||
      post._embedded?.['wp:featuredmedia']?.[0]?.source_url ||
      '',
    excerpt: post.excerpt
      ? (post.excerpt.rendered ?? post.excerpt)
        .replace(/<[^>]+>/g, '')
        .slice(0, excerptLength) + '...'
      : '',
  };
}

/**
 * GraphQL helper
 */
async function getPostsByCategoryAndTag(
  categorySlug: string,
  tagSlug: string,
  limit: number = 3
): Promise<NewsItem[]> {
  const query = `
    query GetInklusiPosts($category: String, $tag: String, $limit: Int) {
      posts(where: { categoryName: $category, tag: $tag }, first: $limit) {
        nodes {
          databaseId
          slug
          title
          date
          excerpt
          featuredImage {
            node {
              sourceUrl
            }
          }
        }
      }
    }
  `;

  try {
    const data = await fetchGraphQL(query, {
      category: categorySlug,
      tag: tagSlug,
      limit,
    });

    const nodes = data?.posts?.nodes;
    if (Array.isArray(nodes) && nodes.length > 0) {
      return nodes.map((post: any) =>
        formatNewsItem(post, 'Program Inklusi', 150)
      );
    }
  } catch (e) {
    console.error('[wordpress] GraphQL query for Inklusi Posts failed:', e);
  }

  // Fallback: REST API
  try {
    const [categories, tags] = await Promise.all([
      fetchAPI(`/categories?slug=${categorySlug}`),
      fetchAPI(`/tags?slug=${tagSlug}`),
    ]);

    if (Array.isArray(categories) && categories.length > 0) {
      const categoryId = categories[0].id;
      let endpoint = `/posts?categories=${categoryId}&per_page=${limit}&_embed`;

      if (Array.isArray(tags) && tags.length > 0) {
        endpoint += `&tags=${tags[0].id}`;
      }

      const posts = await fetchAPI(endpoint);
      if (Array.isArray(posts) && posts.length > 0) {
        return posts.map((post: any) =>
          formatNewsItem(post, 'Program Inklusi', 150)
        );
      }
    }
  } catch (e) {
    console.error(`[wordpress] REST API fallback for Inklusi Posts failed:`, e);
  }

  return [];
}

// =========================================================================
// PUBLIC GRAPHQL SERVICES
// =========================================================================

export async function getInklusiNews(): Promise<NewsItem[]> {
  return getPostsByCategoryAndTag('program-inklusi', 'hero', 3);
}

export async function getInklusiInfo(): Promise<NewsItem[]> {
  return getPostsByCategoryAndTag(
    'program-inklusi',
    'informasi-program-ppa',
    1
  );
}

export async function getInklusiCampaign(): Promise<NewsItem[]> {
  return getPostsByCategoryAndTag(
    'program-inklusi',
    'bahan-kampanye-inklusi',
    4
  );
}

// =========================================================================
// REST API – PAGINATION SUPPORT
// =========================================================================

export async function getInklusiCampaignPage(
  page: number,
  limit: number
): Promise<NewsItem[]> {
  const [categories, tags] = await Promise.all([
    fetchAPI('/categories?slug=program-inklusi'),
    fetchAPI('/tags?slug=bahan-kampanye-inklusi'),
  ]);

  if (!Array.isArray(categories) || categories.length === 0) return [];

  const categoryId = categories[0].id;
  const tagId = Array.isArray(tags) && tags.length > 0 ? tags[0].id : null;

  let endpoint = `/posts?categories=${categoryId}&per_page=${limit}&page=${page}&_embed`;
  if (tagId) endpoint += `&tags=${tagId}`;

  const posts = await fetchAPI(endpoint);
  if (!Array.isArray(posts)) return [];

  return posts.map((post: any) =>
    formatNewsItem(post, 'Kampanye', 100)
  );
}


export async function getPostAttachments(postId: string): Promise<string[]> {
  const media = await fetchAPI(`/media?parent=${postId}`);
  if (Array.isArray(media)) return media.map((m: any) => m.source_url);
  return [];
}

/**
 * Mengambil artikel terkait berdasarkan kategori yang sama.
 * Mengecualikan artikel yang sedang dilihat (excludeId).
 */
export async function getRelatedPosts(
  categoryName: string,
  excludeId: string,
  limit: number = 3,
  tags: string[] = []
): Promise<NewsItem[]> {
  // Coba GraphQL terlebih dahulu
  try {
    const hasTags = tags && tags.length > 0;
    const query = hasTags
      ? `query GetRelated($cat: String, $tags: [String], $limit: Int) {
          posts(where: { categoryName: $cat, tagSlugIn: $tags }, first: $limit) {
            nodes {
              databaseId slug title date excerpt
              categories { nodes { name } }
              featuredImage { node { sourceUrl } }
            }
          }
        }`
      : `query GetRelated($cat: String, $limit: Int) {
          posts(where: { categoryName: $cat }, first: $limit) {
            nodes {
              databaseId slug title date excerpt
              categories { nodes { name } }
              featuredImage { node { sourceUrl } }
            }
          }
        }`;

    const variables: any = { cat: categoryName, limit: limit + 2 };
    if (hasTags) variables.tags = tags;

    let data = await fetchGraphQL(query, variables);
    let nodes = data?.posts?.nodes || [];

    // Jika dengan tags tidak ada hasil, fallback ke category saja
    if (hasTags && nodes.length <= 1) {
      const fallbackQuery = `query GetRelated($cat: String, $limit: Int) {
          posts(where: { categoryName: $cat }, first: $limit) {
            nodes {
              databaseId slug title date excerpt
              categories { nodes { name } }
              featuredImage { node { sourceUrl } }
            }
          }
        }`;
      data = await fetchGraphQL(fallbackQuery, { cat: categoryName, limit: limit + 2 });
      nodes = data?.posts?.nodes || [];
    }

    if (nodes.length > 0) {
      const filtered = nodes
        .filter((p: any) => p.databaseId.toString() !== excludeId)
        .slice(0, limit);
      if (filtered.length > 0) {
        return filtered.map((p: any) => ({
          id: p.databaseId.toString(),
          slug: p.slug || p.databaseId.toString(),
          title: sanitizeHTML(p.title),
          date: new Date(p.date).toLocaleDateString('id-ID', DATE_OPTIONS),
          category: p.categories?.nodes[0]?.name || 'Umum',
          imageUrl: p.featuredImage?.node?.sourceUrl || '',
          excerpt: p.excerpt
            ? sanitizeHTML(p.excerpt).replace(/<[^>]+>/g, '').slice(0, 120) + '...'
            : '',
        }));
      }
    }
  } catch (e) {
    console.error('[wordpress] GraphQL related posts failed:', e);
  }

  // Fallback: REST API
  try {
    const cats = await fetchAPI(`/categories?slug=${encodeURIComponent(categoryName)}`);
    if (Array.isArray(cats) && cats.length > 0) {
      const posts = await fetchAPI(
        `/posts?categories=${cats[0].id}&per_page=${limit + 2}&exclude=${excludeId}&_embed`
      );
      if (Array.isArray(posts) && posts.length > 0) {
        return posts.slice(0, limit).map((post: any) => ({
          id: post.id.toString(),
          slug: post.slug || post.id.toString(),
          title: sanitizeHTML(post.title.rendered),
          date: new Date(post.date).toLocaleDateString('id-ID', DATE_OPTIONS),
          category: post._embedded?.['wp:term']?.[0]?.[0]?.name || 'Umum',
          imageUrl: extractFeaturedImage(post),
          excerpt: post.excerpt?.rendered
            ? sanitizeHTML(post.excerpt.rendered).replace(/<[^>]+>/g, '').slice(0, 120) + '...'
            : '',
        }));
      }
    }
  } catch (e) {
    console.error('[wordpress] REST related posts failed:', e);
  }

  return [];
}

//2. THINKERS / TEAM (VIA GRAPHQL)
// =========================================================================
// Gunakan Map (bukan plain object) untuk menghindari prototype pollution (CWE-94)
// Bracket notation pada plain object memungkinkan akses ke __proto__ / constructor
const ROLE_PRIORITY = new Map<string, number>([
  ['Ketua', 1],
  ['Wakil Ketua 1', 2],
  ['Wakil Ketua', 3],
  ['Sekertaris 1', 4],
  ['Sekertaris', 5],
  ['Anggota', 6],
]);
const categorySlugs = [
  'ketua',
  'wakil-ketua-1',
  'wakil-ketua',
  'sekertaris-1', 'sekertaris', 'anggota',
];
export async function getTeamMembers(): Promise<NewsItem[]> {
  // Hindari timeout saat react-snap berjalan
  if (typeof navigator !== 'undefined' && navigator.userAgent === 'ReactSnap') {
    return STAFF_MEMBERS.length > 0 ? STAFF_MEMBERS : [];
  }

  // Gunakan REST API untuk fetching team members karena lebih mudah untuk multiple categories saat ini  
  const postsByCategories = await Promise.all(categorySlugs.map((slug) => getPostsByCategory(slug, 50)));
  const posts = postsByCategories.flat();
  if (posts.length === 0) {
    return STAFF_MEMBERS.length > 0 ? STAFF_MEMBERS : [];
  }
  // Deduplikasi berdasarkan ID
  const uniquePosts = Array.from(new Map(posts.map(item => [item.id, item])).values());
  const team = uniquePosts.map(p => {
    // Kategori di WP dianggap sebagai Role    
    const rawRole = p.category === 'Cabang' || p.category === 'Umum' ? 'Anggota' : p.category;
    let displayRole = rawRole;
    if (rawRole === 'WAKIL KETUA 1') displayRole = 'WAKIL KETUA';
    if (rawRole === 'SEKERTARIS 1') displayRole = 'SEKERTARIS';
    return {
      ...p, originalRole: rawRole,
      role: displayRole,
    };
  });
  // Logika Sorting — gunakan Map.get() yang aman dari prototype pollution
  return team.sort((a, b) => {
    const priorityA = ROLE_PRIORITY.get(a.originalRole || '') ?? 99;
    const priorityB = ROLE_PRIORITY.get(b.originalRole || '') ?? 99;
    return priorityA - priorityB;
  });
}

export async function getActivities(): Promise<Activity[]> {
  const posts = await getPostsByCategory('kegiatan', 4);
  return posts.map(p => ({ id: p.id, title: p.title, date: p.date, location: 'Gedung PBNU', type: 'Event' }));
}